/**
 * Controlled vocabularies of the CLM contract.
 *
 * ## How to read this file
 *
 * Several vocabularies here are **only partly ratified**. Where that is the case
 * the values are split into two exported constants — `*_RATIFIED` and
 * `*_PROVISIONAL` — and the schema is the union of both. Nothing is invented:
 * every provisional value is cited to the document it came from. The split
 * exists so that a reader can tell, without leaving this file, which half of a
 * vocabulary a reviewer still has to sign off.
 *
 * `docs/architecture-design-diag.md` §7 says this outright: *"Decision needed
 * before PR-03/PR-05. The schema encodes these as CHECK constraints, and §8.1
 * Rules 1–4 are enforced against them in triggers — so the vocabulary has to be
 * settled before the tables are written, not after."*
 */
import { z } from 'zod';
/* -------------------------------------------------------------------------- */
/* Party type — DR-04 / IR-02 / IR-03                                          */
/* -------------------------------------------------------------------------- */
/** Ratified in full. `architecture.md` §7.3, `technical-decisions.md` TD-02. */
export const PARTY_TYPES = ['Client', 'Partner', 'Vendor'];
export const PartyTypeSchema = z.enum(PARTY_TYPES);
/**
 * System of record for party master data. Ratified: IR-02/IR-03, enforced in the
 * database by `ck_party_sor` (`architecture.md` §7.3) — a Vendor physically
 * cannot resolve to Qfly, and a Client cannot resolve to Dynamics. `party.ts`
 * mirrors that constraint in the type system rather than in a comment.
 */
export const SOR_SYSTEMS = ['Qfly', 'Dynamics'];
export const SorSystemSchema = z.enum(SOR_SYSTEMS);
/** Role a party plays on one contract. `wp-01` §3.4 `ContractParty.PartyRole`. */
export const CONTRACT_PARTY_ROLES = ['Counterparty', 'Partner', 'Subcontractor'];
export const ContractPartyRoleSchema = z.enum(CONTRACT_PARTY_ROLES);
/* -------------------------------------------------------------------------- */
/* Contract type                                                               */
/* -------------------------------------------------------------------------- */
/**
 * Ratified: `MSA`, `NDA`, `SOW` — the three agreement kinds this system exists
 * to track (CLAUDE.md, "MSAs, NDAs and SOWs").
 */
export const CONTRACT_TYPES_RATIFIED = ['MSA', 'NDA', 'SOW'];
/**
 * TODO(CLM-14): vocabulary pending ratification — `AMENDMENT` and `OTHER`.
 *
 * Two ratified documents disagree about whether an amendment is a contract
 * *type* or purely a *relationship*:
 *
 *  - `docs/wp-01-schema-tsql-superseded.md` §3.1 registers `AMENDMENT` and
 *    `OTHER` as contract types and adds `CK_Contract_AmendmentHasTarget`
 *    ("an AMENDMENT must name what it amends"), and `docs/wp-03-extraction-provider.md`
 *    §2 repeats `'MSA' | 'NDA' | 'SOW' | 'AMENDMENT' | 'OTHER'`.
 *  - `docs/technical-decisions.md` TD-04 keys an amendment as
 *    `SGW-MSA-0003-A1` — i.e. it keeps the **parent's** type segment, which is
 *    incompatible with `AMENDMENT` being the type segment of its own key.
 *
 * Both are accepted here so neither downstream reading breaks. Resolve before
 * the `contract_type_ref` seed data is written.
 */
export const CONTRACT_TYPES_PROVISIONAL = ['AMENDMENT', 'OTHER'];
export const CONTRACT_TYPES = [...CONTRACT_TYPES_RATIFIED, ...CONTRACT_TYPES_PROVISIONAL];
export const ContractTypeSchema = z.enum(CONTRACT_TYPES);
/** The subset a guided upload may select (FR-13/FR-14). */
export const ContractTypeRatifiedSchema = z.enum(CONTRACT_TYPES_RATIFIED);
/**
 * CLM-110 / DR-10 — how a contract's **stored** type was decided, as
 * `clm.contract.contract_type_source` records it (`0104`, widened by CLM-110's
 * decision migration).
 *
 *  - `derived` — a deterministic intake rule: a file-name or folder token
 *    (`detect.ts`, the SharePoint planner), a SOW number, the relationship
 *    (`supersedes` takes its target's type), or the `OTHER` fallback;
 *  - `manual` — an Admin decided a type that is neither the AI's reading nor the
 *    provisional one, or a person chose the type before CLM-118 (the guided
 *    upload's hint, a planner override, a bundle row whose type differs from
 *    what its path detects). Since CLM-118 (R-118-18) nobody uploading picks a
 *    type, so the Admin's decision is the only writer;
 *  - `ai_proposed` — the AI's reading: applied as the **provisional** working
 *    type by the pipeline's case 2 (D-110-3), where there is no trusted
 *    file-name type, or taken by an Admin's decision (`confirmed`). The schema
 *    requires the stored type to equal the proposal and never allows it while
 *    `pending` or `auto_confirmed` (`ck_contract_type_ai_proposed_settled`). It is
 *    settled only with a person's `HumanVerification` row (CR-08) — see
 *    {@link CONTRACT_TYPE_PROPOSALS};
 *  - `inherited` — an amendment, whose type follows the agreement it amends
 *    (R-110-15). Amendments only (`ck_contract_type_source_inherited`).
 *
 * Whether a person **settled** the type is a separate axis —
 * {@link CONTRACT_TYPE_CONFIRMATIONS}. `null` on the wire means *recorded before
 * CLM-110*: no backfill guesses it. Deliberately a separate vocabulary from the
 * ingest wire's two-value `CONTRACT_TYPE_SOURCES` (`plan-sharepoint-ingest.ts`),
 * which describes a plan row and can never be `ai_proposed` or `inherited`.
 */
export const CONTRACT_TYPE_DECIDED_BY = ['derived', 'manual', 'ai_proposed', 'inherited'];
export const ContractTypeDecidedBySchema = z.enum(CONTRACT_TYPE_DECIDED_BY);
/**
 * CLM-110 (R-110-13), CLM-118 (R-118-1, R-118-2) — whether a contract's type is
 * settled, and by what, as `clm.contract.contract_type_confirmation` records it.
 * Appended in this order: enum order is API surface.
 *
 *  - `provisional` — not settled: a file-name type, a person's type, or the AI's
 *    type applied as the working type (case 2), until an Admin decides or the
 *    file name and the AI agree. Every contract filed before CLM-110's decision
 *    starts provisional, which is a fact, not a guess;
 *  - `confirmed` — an Admin's decision: names its `HumanVerification` row;
 *  - `pending` — **Type pending**: the stored `OTHER` is a placeholder, because
 *    neither the file name nor a person gave a type. Never counted, filtered or
 *    shown as `OTHER` (B19);
 *  - `auto_confirmed` — settled with **no person**: the file-name rule gave MSA,
 *    SOW or NDA and the AI's reading agreed (D-110-3, 1a), or approved by
 *    policy — auto-approval for AI-decided types (D-110-4, CLM-121). Names a
 *    `contract-type-rule` audit row that names nobody, never a decision. A reader
 *    that does not know this value must read it as *not* confirmed.
 *
 * An amendment carries its agreement's (`fk_contract_amends_type`).
 */
export const CONTRACT_TYPE_CONFIRMATIONS = ['provisional', 'confirmed', 'pending', 'auto_confirmed'];
export const ContractTypeConfirmationSchema = z.enum(CONTRACT_TYPE_CONFIRMATIONS);
/** CLM-118 — the confirmations under which a type is not yet settled: a reason is computed for each. */
export const CONTRACT_TYPE_UNSETTLED_CONFIRMATIONS = ['provisional', 'pending'];
/** CLM-118 — whether a confirmation is settled (`confirmed` by an Admin, or `auto_confirmed`). */
export function isSettledTypeConfirmation(confirmation) {
    return confirmation === 'confirmed' || confirmation === 'auto_confirmed';
}
/**
 * CLM-118 (R-118-3, R-118-5) — what the deterministic file-name rule found at
 * intake, as `clm.contract.contract_type_name_evidence` records it:
 *
 *  - `typed` — a type word (`MSA`, `NDA`, `SOW`) or a SOW-shaped number in the
 *    name, the type of the contract amended, or that of a contract replaced
 *    when its type was settled by an Admin or by 1a, never by the policy
 *    (R-121-21), or itself a trusted file-name type;
 *  - `untyped` — none: a placeholder, a SOW typed only by a person's number, or
 *    a replacement of a type no file name or person verified (R-118-4);
 *  - `conflicting` — the name contradicts itself (two type words, or an MSA/NDA
 *    word beside a SOW number), so there is no trusted file-name type.
 *
 * `null` on the wire = filed before CLM-118, or a type whose origin was never
 * recorded. Written once at intake; a decision never changes it.
 */
export const CONTRACT_TYPE_NAME_EVIDENCE = ['typed', 'untyped', 'conflicting'];
export const ContractTypeNameEvidenceSchema = z.enum(CONTRACT_TYPE_NAME_EVIDENCE);
/**
 * CLM-118 (R-118-10, B1) — which case of D-110-3 a type write or decision came
 * from, recorded as `type_case` on every automatic act row and every Admin
 * decision, so the 0.900 figure can be computed whichever way point 6 lands:
 *
 *  - `agree` / `disagree` — a trusted file-name type, and the AI's reading
 *    agreed with it (1a) or did not (1b);
 *  - `no_name_type` — the name gave no type (evidence `untyped`);
 *  - `conflicting_names` — the name contradicted itself (evidence `conflicting`);
 *  - `name_evidence_unknown` — the name's evidence was never recorded: a
 *    person's type, a legacy type, a row filed before CLM-118. Never claimed as
 *    `no_name_type`;
 *  - `no_reading` — the Admin decided with no AI reading;
 *  - `after_settled` — the type was already settled (`confirmed` or
 *    `auto_confirmed`).
 *
 * A safety sample is marked `safety_sample: true` on the decision, never a case
 * (CLM-121, R-121-10).
 */
export const CONTRACT_TYPE_RULE_CASES = [
    'agree',
    'disagree',
    'no_name_type',
    'conflicting_names',
    'name_evidence_unknown',
    'no_reading',
    'after_settled',
];
export const ContractTypeRuleCaseSchema = z.enum(CONTRACT_TYPE_RULE_CASES);
/**
 * CLM-118 (R-118-8) — the automatic writes of the type rule, as the act row's
 * `type_rule` records them: `file_name_and_ai_agree` (1a, confirms) and
 * `ai_type_applied` (case 2, applies the AI's type as the provisional working
 * type). CLM-121 (R-121-1) appends `auto_approval_policy`: case 2 applied and
 * approved by policy (`auto_confirmed`), with no person.
 */
export const CONTRACT_TYPE_RULES = ['file_name_and_ai_agree', 'ai_type_applied', 'auto_approval_policy'];
export const ContractTypeRuleSchema = z.enum(CONTRACT_TYPE_RULES);
/** CLM-118 (R-118-8) — who ran an act row: the pipeline's type step, or `0109`'s deploy backfill (B34). */
export const CONTRACT_TYPE_RULE_APPLIED_BY = ['pipeline', 'deploy_backfill'];
export const ContractTypeRuleAppliedBySchema = z.enum(CONTRACT_TYPE_RULE_APPLIED_BY);
/**
 * CLM-118 (§3.4) — every outcome of the pipeline's type rule (`planTypeRule`),
 * for its log line and its test: the two writes, then each `leave:` outcome.
 */
export const CONTRACT_TYPE_RULE_OUTCOMES = [
    'confirm_agreement',
    'apply_ai_type',
    'amendment',
    'restricted',
    'settled',
    'superseded',
    'disagreement',
    'ai_no_type',
    'ai_sow_needs_number',
    'nothing_to_apply',
    'sow_number_recorded',
    'not_replaceable',
    'typed_key',
    'relationship_clash',
];
export const ContractTypeRuleOutcomeSchema = z.enum(CONTRACT_TYPE_RULE_OUTCOMES);
/**
 * CLM-118 — what settled a type with no Admin, as the record's
 * `auto_confirmation.kind` shows it (read from the act row's `type_rule`, never
 * from a `decision_kind`). CLM-121 appends `auto_approval_policy`, so a kind is
 * its act row's `type_rule` ("approved by policy").
 */
export const CONTRACT_TYPE_AUTO_CONFIRMATION_KINDS = ['file_name_and_ai_agree', 'auto_approval_policy'];
export const ContractTypeAutoConfirmationKindSchema = z.enum(CONTRACT_TYPE_AUTO_CONFIRMATION_KINDS);
/**
 * CLM-121 (D-110-4 point 3, R-121-3) — the four numbers of the contract type's
 * auto-approval, **fixed by D-110-4; never settings** (they are not read from
 * the co-signed `verification_policy_setting`, Gerry's 1943297). Restated as
 * literals in `0113`'s CHECKs:
 *
 *  - **strictly above** 0.900 (`threshold_milli`: `verified × 1000 > 900 × n`,
 *    compared in integers — 27 of 30 does not qualify, 28 of 30 does);
 *  - at least 30 counted decisions;
 *  - 1 in 5 eligible types still sent to an Admin (`sample_cutoff`: a bucket
 *    below 200 of 1000);
 *  - 90 days without a counted check pauses it.
 */
export const CONTRACT_TYPE_AUTO_APPROVAL_FIGURES = Object.freeze({
    threshold: '0.900',
    threshold_milli: 900,
    minimum_sample: 30,
    sample_rate: '0.200',
    sample_cutoff: 200,
    stale_after_days: 90,
});
/**
 * CLM-121 (R-121-11) — why a case-2 type was approved or went to the Admin, as
 * every pipeline act row's `auto_approval.disposition` records it: approved by
 * policy, the safety sample, not eligible (with its reason), or the state that
 * held it (`decideTypeAutoApproval`, first match wins). `0113`'s shape CHECK
 * holds the same list.
 */
export const CONTRACT_TYPE_AUTO_APPROVAL_DISPOSITIONS = [
    'auto_approved',
    'sampled',
    'not_eligible',
    'switched_off',
    'lapsed',
    'paused_by_correction',
    'too_few_decisions',
    'stale',
    'below_threshold',
];
export const ContractTypeAutoApprovalDispositionSchema = z.enum(CONTRACT_TYPE_AUTO_APPROVAL_DISPOSITIONS);
/**
 * CLM-121 (§3.4, R-121-5, R-121-6) — why an AI-decided type is never approved
 * by policy, in the order they are tested: OTHER (never MSA or NDA); a name
 * that contradicts itself; a name that gave a type; a flagged possible
 * amendment; a vendor-party contract. `0113`'s shape CHECK holds the same list.
 */
export const CONTRACT_TYPE_AUTO_APPROVAL_NOT_ELIGIBLE = [
    'ai_type_other',
    'conflicting_names',
    'name_typed',
    'amendment_signal',
    'vendor_party',
];
export const ContractTypeAutoApprovalNotEligibleSchema = z.enum(CONTRACT_TYPE_AUTO_APPROVAL_NOT_ELIGIBLE);
/**
 * CLM-121 (§3.5) — the switch's state as the Auto-approval page shows it: the
 * pipeline's decision with the sample step dropped (`on` covers sampled and
 * approved), plus `not_read` for no pipeline reading of the type in scope.
 * TypeScript only: a unit test pins it to `typeAutoApprovalState`'s outputs.
 */
export const CONTRACT_TYPE_AUTO_APPROVAL_STATES = [
    'on',
    'switched_off',
    'not_read',
    'lapsed',
    'paused_by_correction',
    'too_few_decisions',
    'stale',
    'below_threshold',
];
export const ContractTypeAutoApprovalStateSchema = z.enum(CONTRACT_TYPE_AUTO_APPROVAL_STATES);
/**
 * CLM-121 (R-121-9) — what paused the switch: an Admin corrected a safety
 * sample, or corrected a type the policy approved. `fn_contract_type_policy_pause`
 * returns the same two.
 */
export const CONTRACT_TYPE_AUTO_APPROVAL_PAUSE_KINDS = ['corrected_sample', 'corrected_policy_approval'];
export const ContractTypeAutoApprovalPauseKindSchema = z.enum(CONTRACT_TYPE_AUTO_APPROVAL_PAUSE_KINDS);
/**
 * CLM-121 (D-110-4 point 6 (b), R-121-4) — the cases whose decisions the
 * auto-approval figure counts: only those on contracts whose file name gave no
 * type. `no_name_type` counts whole. `name_evidence_unknown` says only that no
 * name evidence was recorded — a person's type, a legacy type or a deploy-window
 * row (R-118-10) — so it counts only under the narrowing of
 * {@link CONTRACT_TYPE_AUTO_APPROVAL_UNKNOWN_EVIDENCE_SOURCES}. `disagree` is
 * "AI against the file name", its own figure; `agree`, `conflicting_names`,
 * `after_settled` and `no_reading` are left out.
 * `fn_contract_type_policy_basis` holds the same list.
 */
export const CONTRACT_TYPE_AUTO_APPROVAL_COUNTED_CASES = ['no_name_type', 'name_evidence_unknown'];
/**
 * CLM-121 (D-110-4 point 6 (b), R-121-4) — when a `name_evidence_unknown`
 * decision counts: its row records no name evidence, and the provisional type it
 * decided was a person's (`manual`) or the AI's (`ai_proposed`, which case 2
 * applies only over a person's type or a placeholder), or intake's `derived`
 * `OTHER` fallback. A legacy type (`NULL` source) or a `derived` MSA, NDA or SOW
 * with no evidence is a file-name or legacy type (R-118-9), outside 6b, until
 * CLM-119 records which of them a person typed. `fn_contract_type_policy_basis`
 * holds the same list and the `derived` `OTHER` arm.
 */
export const CONTRACT_TYPE_AUTO_APPROVAL_UNKNOWN_EVIDENCE_SOURCES = ['manual', 'ai_proposed'];
/** CLM-121 (D-110-4 point 3) — the types auto-approval may approve: MSA and NDA (NDAs included), never SOW or OTHER. */
export const CONTRACT_TYPE_AUTO_APPROVAL_TYPES = ['MSA', 'NDA'];
/**
 * CLM-118 (R-118-14, B13) — the word in a file or folder name that suggests an
 * amendment filed on its own: `amendment(s)`, `addendum` / `addenda`,
 * `change order(s)`, `change request(s)`, `variation(s)`, `extension(s)`, or a
 * CR-suffixed SOW number (`cr_sow_number`).
 */
export const AMENDMENT_SIGNAL_NAMES = [
    'amendment',
    'addendum',
    'change_order',
    'change_request',
    'variation',
    'extension',
    'cr_sow_number',
];
export const AmendmentSignalNameSchema = z.enum(AMENDMENT_SIGNAL_NAMES);
/**
 * CLM-110 (R-110-13, R-110-28), CLM-118 (R-118-15) — **why** a type is not yet
 * settled: set while `provisional` or `pending`. Computed by the API from the
 * stored rows, never stored (a stored cause would go stale whenever the
 * pipeline moved):
 *
 *  - `awaiting_ai` — not restricted, and not yet past extraction;
 *  - `ai_agrees` — the AI's latest reading equals the type;
 *  - `ai_disagrees` — the AI read another of the four types;
 *  - `ai_no_type` — the AI answered outside the vocabulary: a reading with no
 *    usable type, listed under "AI disagrees" (R-110-28);
 *  - `restricted` — `processing_restricted`: never read by an AI (rule 4);
 *  - `no_ai_proposal` — past extraction with no reading: extracted before 1.5.0,
 *    a refusal, or a document that could not be converted;
 *  - `ai_sow_needs_number` — a Type pending agreement the AI read as a SOW: an
 *    AI SOW is never applied without its number (D-71-0), so the Admin adds it;
 *  - `ai_type_not_applied` — a Type pending agreement the AI read as MSA, NDA or
 *    OTHER, whose type a guard held (R-118-9): the record says which;
 *  - `awaiting_rerun` — `ExtractionFailed` and admitted to a re-run (CLM-79's
 *    precondition): waiting for a re-run or an alias decision.
 *
 * The two Type pending reasons are computed for a `pending` agreement only; an
 * amendment under one keeps the provisional ladder over its own row (B17).
 */
export const CONTRACT_TYPE_PROVISIONAL_REASONS = [
    'awaiting_ai',
    'ai_agrees',
    'ai_disagrees',
    'ai_no_type',
    'restricted',
    'no_ai_proposal',
    'ai_sow_needs_number',
    'ai_type_not_applied',
    'awaiting_rerun',
];
export const ContractTypeProvisionalReasonSchema = z.enum(CONTRACT_TYPE_PROVISIONAL_REASONS);
/**
 * CLM-110 (R-110-17, R-110-18, R-110-20) — what an Admin's decision did, as its
 * `HumanVerification` row records it in `decision_kind`:
 *
 *  - `accepted_ai` — the decided type is the AI's latest reading: the Admin took
 *    it over a different provisional type, or confirmed a provisional type the AI
 *    agreed with. The source becomes `ai_proposed` (R-110-20);
 *  - `kept_against_ai` — kept the provisional type although the AI read another
 *    one, or gave no usable type (R-110-28);
 *  - `corrected` — set a type that is neither the AI's reading nor the
 *    provisional one;
 *  - `confirmed` — kept the provisional type with no AI reading behind it
 *    (restricted, or never read);
 *  - `typed` — CLM-118 (R-118-10): replaced a Type pending placeholder with a
 *    type that is not the AI's reading. A placeholder is never "kept".
 */
export const CONTRACT_TYPE_DECISION_KINDS = ['accepted_ai', 'kept_against_ai', 'corrected', 'confirmed', 'typed'];
export const ContractTypeDecisionKindSchema = z.enum(CONTRACT_TYPE_DECISION_KINDS);
/**
 * CLM-110 / FR-34 — what the classifier may **propose** for a document
 * (R-110-1), and what an Admin may decide a type to be. `AMENDMENT` is not
 * proposable (R-110-2): an amendment's type is its agreement's — since CLM-110's
 * decision migration the schema holds that (`fk_contract_amends_type`), so
 * `AMENDMENT` can no longer be stored at all — and an amendment's own reading is
 * shown under its agreement and counted as not compared, never as a
 * disagreement of its own.
 */
export const CONTRACT_TYPE_PROPOSALS = ['MSA', 'NDA', 'SOW', 'OTHER'];
export const ContractTypeProposalSchema = z.enum(CONTRACT_TYPE_PROPOSALS);
/* -------------------------------------------------------------------------- */
/* Two-axis status — TD-04                                                     */
/* -------------------------------------------------------------------------- */
/**
 * Axis 1 of 2 — the **legal** state. Answers *"which agreement governs?"* and
 * drives §8.1 Rules 1–3.
 *
 * This is deliberately separate from `processing_status`. TD-04: *"a contract can
 * be legally `Active` and governing while its renewal date is still
 * `PendingVerification`. One status column forces a false choice."* Never
 * collapse the two.
 *
 * Ratified values only.
 */
export const LIFECYCLE_STATUS_RATIFIED = [
    /**
     * Signed. Set on DocuSign completion — `architecture.md` §2.4.1. Every intake path writes it; an
     * amendment may target it (D-124-1).
     */
    'Executed',
    /**
     * Governing. §8.1 Rule 1 (D-124-1): an amendment may target an `Executed` or `Active` agreement.
     * Written today only by `usp_record_supersession`.
     */
    'Active',
    /** Replaced. §8.1 Rule 2 flips the predecessor to this atomically; Rule 3 makes it read-only. */
    'Superseded',
];
/**
 * TODO(CLM-14): vocabulary pending ratification — `Draft`, `Expired`,
 * `Terminated`.
 *
 * `docs/technical-decisions.md` TD-04 lists all six values as decided, and
 * `wp-01` §3.3 writes them into `CK_Contract_Lifecycle`. But
 * `docs/architecture-design-diag.md` §7 marks `Expired` and `Terminated`
 * 🔴 **ratify** — *"inferred; retention runs from termination date, so a
 * terminated state must exist"* — and does not model `Draft` in the state
 * machine at all (it starts at `Executed`).
 *
 * `Draft` is the one worth arguing about: this system indexes *executed*
 * contracts, and contract generation is explicitly out of scope, so it is not
 * obvious that a `Draft` state is reachable at all.
 */
export const LIFECYCLE_STATUS_PROVISIONAL = ['Draft', 'Expired', 'Terminated'];
export const LIFECYCLE_STATUSES = [
    ...LIFECYCLE_STATUS_RATIFIED,
    ...LIFECYCLE_STATUS_PROVISIONAL,
];
export const LifecycleStatusSchema = z.enum(LIFECYCLE_STATUSES);
export const LifecycleStatusRatifiedSchema = z.enum(LIFECYCLE_STATUS_RATIFIED);
/**
 * Axis 2 of 2 — the **processing** state. Answers *"is the data about this
 * contract trustworthy yet?"* and drives the verification queue and the aging
 * signal.
 *
 * Ratified values only.
 */
export const PROCESSING_STATUS_RATIFIED = [
    /** Row exists, pipeline has not run. `architecture.md` §2.4.1, §2.4.2. */
    'PendingExtraction',
    /**
     * `processing_restricted` contract (§4C). Stored, converted in-tenant, indexed
     * and searchable — facts are entered by hand. Only the *reasoning* step is
     * withheld. A contract in this state must never reach an LLM.
     */
    'manual_entry_pending',
];
/**
 * TODO(CLM-14): vocabulary pending ratification — `Converted`, `Pseudonymised`,
 * `Extracted`, `PendingVerification`, `Verified`, `ExtractionFailed`.
 *
 * `docs/architecture-design-diag.md` §7 marks the first five 🔴 **ratify** —
 * they are *"inferred from the pipeline stages"* and appear only in that
 * document's state diagram. `ExtractionFailed` appears only in
 * `docs/technical-decisions.md` TD-04 and `wp-01` `CK_Contract_Processing`,
 * which in turn do **not** list the three intermediate pipeline states.
 *
 * Two substantive questions for the reviewer, not just a list to tick:
 *
 *  1. **Is `processing_status` a pipeline tracker or a data-trust flag?** TD-04
 *     says data-trust and lists four values. The §7 state machine says pipeline
 *     and lists eight. Those are different fields wearing one name.
 *  2. **Casing is inconsistent in the ratified half.** `manual_entry_pending` is
 *     snake_case (`architecture.md` §4C); every other value is PascalCase. These
 *     are enum *values*, not identifiers, so CLAUDE.md rule 12 does not settle
 *     it. Pick one before the CHECK constraint is written — a mixed-casing
 *     vocabulary will be got wrong by hand at least once.
 */
export const PROCESSING_STATUS_PROVISIONAL = [
    'Converted',
    'Pseudonymised',
    'Extracted',
    'PendingVerification',
    'Verified',
    'ExtractionFailed',
];
export const PROCESSING_STATUSES = [
    ...PROCESSING_STATUS_RATIFIED,
    ...PROCESSING_STATUS_PROVISIONAL,
];
export const ProcessingStatusSchema = z.enum(PROCESSING_STATUSES);
export const ProcessingStatusRatifiedSchema = z.enum(PROCESSING_STATUS_RATIFIED);
/**
 * The **trust** axis — B-02's third column, `clm.contract.data_trust`
 * (`ck_contract_data_trust`). Independent of `processing_status`: a fully
 * pipelined contract can still be unverified, and a `manual_entry_pending`
 * contract can be fully trusted.
 *
 * TD-04's four values verbatim, plus `AutoApproved` (CLM-108, D-2.1-4, decided
 * 1 Oct 2026): some settled field on the contract was auto-approved by policy,
 * so the contract is **never** reported `Verified`. `Verified` means every field
 * was settled by a person — two database triggers hold that from both ends —
 * and it is the only value from which any surface may print "Verified" about a
 * contract. Not `processing_status = 'Verified'` (the end of the pipeline axis,
 * which an auto-approved contract also reaches) and not "nothing outstanding"
 * (also true of a contract with no field row at all).
 *
 * This export closes `packages/db/README.md` deviation 13, which asked for it
 * and for a `data_trust` field on both contract shapes.
 */
export const DATA_TRUST_STATUSES = [
    'PendingExtraction',
    'PendingVerification',
    'Verified',
    'ExtractionFailed',
    'AutoApproved',
];
export const DataTrustSchema = z.enum(DATA_TRUST_STATUSES);
/* -------------------------------------------------------------------------- */
/* Obligation                                                                  */
/* -------------------------------------------------------------------------- */
/**
 * TODO(CLM-14): vocabulary pending ratification. These values are cited only to
 * `docs/wp-01-schema-tsql-superseded.md` §3.5 (`CK_Obligation_Status`) — a file
 * CLAUDE.md rule 12 designates "history only". No ratified document restates
 * them. They are carried forward because FR-03 requires an obligation status and
 * nothing else defines one.
 */
export const OBLIGATION_STATUSES = ['Open', 'InProgress', 'Met', 'Waived', 'Breached'];
export const ObligationStatusSchema = z.enum(OBLIGATION_STATUSES);
/**
 * Owner resolution state — `architecture.md` §3.6. Ratified, and the important
 * value is `unresolved`: *"a visible state, not a silent default."* Extraction
 * returns a role string ("the Supplier's project lead"); a notification needs a
 * mailbox; unresolved obligations appear on the admin dashboard and route to the
 * contract owner so nothing is dropped.
 */
export const OBLIGATION_OWNER_STATUSES = ['unresolved', 'resolved', 'not_applicable'];
export const ObligationOwnerStatusSchema = z.enum(OBLIGATION_OWNER_STATUSES);
/* -------------------------------------------------------------------------- */
/* Clause matching — §4.2, FR-28                                               */
/* -------------------------------------------------------------------------- */
/**
 * Confidence band. Ratified — `architecture.md` §4.2. The band, not the raw
 * score, decides the route:
 *
 * | Band     | Route |
 * |----------|-------|
 * | `HIGH`   | auto-accept |
 * | `MEDIUM` | review queue |
 * | `LOW`    | record as bespoke, surface as a candidate new library clause |
 *
 * Note the case difference from `wp-03`, which writes `'high' | 'medium' | 'low'`
 * on the *provider* interface. That interface is gateway-internal; this contract
 * uses the upper-case form the architecture uses.
 */
export const CONFIDENCE_BANDS = ['HIGH', 'MEDIUM', 'LOW'];
export const ConfidenceBandSchema = z.enum(CONFIDENCE_BANDS);
/**
 * State of one library clause as it appears in one contract. Ratified —
 * `architecture.md` §4.1/§2.4.3 and `jira-story-draft.md` CLM-P1-02.
 *
 * `Modified` is the value the whole matcher exists to produce: a high cosine
 * score says *same topic*, not *same terms*, and recording an altered liability
 * cap as `Standard` is the one error class that matters (§4.1).
 */
export const CLAUSE_MATCH_STATES = ['Standard', 'Modified', 'Rejected'];
export const ClauseMatchStateSchema = z.enum(CLAUSE_MATCH_STATES);
/** The three embedded views of a clause — WP-09 §4. Ratified. */
export const CLAUSE_REPRESENTATIONS = ['title', 'master_text', 'purpose'];
export const ClauseRepresentationSchema = z.enum(CLAUSE_REPRESENTATIONS);
/** Search relevance judgement — WP-09 §6. Ratified, including the weights. */
export const SEARCH_JUDGEMENTS = ['Preferred', 'Relevant', 'NotRelevant'];
export const SearchJudgementSchema = z.enum(SEARCH_JUDGEMENTS);
/** WP-09 §6 `w(j.judgement)`. Exported so the front end can explain a boost. */
export const SEARCH_JUDGEMENT_WEIGHT = {
    Preferred: 2.0,
    Relevant: 1.0,
    NotRelevant: -1.5,
};
/* -------------------------------------------------------------------------- */
/* Verification — FR-07                                                        */
/* -------------------------------------------------------------------------- */
/**
 * TODO(CLM-14): vocabulary pending ratification. Cited only to
 * `docs/wp-01-schema-tsql-superseded.md` §3.7 (`CK_FieldVer_Status`). FR-07 and
 * `architecture.md` §3.1 require the queue and the aging signal but name no
 * status values.
 *
 * The distinction that matters and should survive ratification is `Verified`
 * (the extracted value was right) versus `Corrected` (a human changed it) —
 * collapsing them would destroy the correction signal that measures NFR-12
 * extraction accuracy.
 *
 * `AutoApproved` is CLM-108's fifth value (CR-06, decided 1 Oct 2026): the
 * field was **settled by policy, not by a person** — its evidence is an
 * `AutoApproval` audit row, never a `HumanVerification` one. It is settled
 * (not outstanding, so FR-32's remaining count excludes it) and it is **not
 * human-verified**: `is_human_verified` stays `Verified | Corrected`. It is
 * terminal, like `Verified`.
 */
export const VERIFICATION_STATUSES = ['Pending', 'Verified', 'Corrected', 'Rejected', 'AutoApproved'];
export const VerificationStatusSchema = z.enum(VERIFICATION_STATUSES);
/* -------------------------------------------------------------------------- */
/* Verification policy — CLM-108 (FR-31, FR-32, NFR-16, CR-06)                 */
/* -------------------------------------------------------------------------- */
/**
 * D-2.1-2, decided 1 Oct 2026, **revised 14 Oct 2026 by Harnoor Gandhi** (30 →
 * 10 — at real review volume the 30-decision bar left almost every field
 * queued indefinitely). A field needs **at least 10 human decisions**
 * (Verified + Corrected) at its current `(field, schema_version, model)` grain
 * before it may auto-approve — under the threshold and under an opt-in alike.
 * A constant, not a setting: `ck_audit_log_auto_approval_justified` and
 * `ck_verification_policy_opt_in` state the same number in the database
 * (`packages/db/migrations/0116_verification_policy_minimum_sample_10.sql`).
 */
export const VERIFICATION_MINIMUM_SAMPLE = 10;
/**
 * `clm.verification_policy.mode` (R-108-3). No policy row means `threshold`.
 *
 *  - `threshold` — auto when n ≥ {@link VERIFICATION_MINIMUM_SAMPLE} and the
 *    rate meets the co-signed threshold.
 *  - `always_verify` — an Admin pin; persists across grains (the safe direction).
 *  - `auto_approve` — an Admin opt-in below the threshold. Still needs
 *    n ≥ {@link VERIFICATION_MINIMUM_SAMPLE} and a note, takes effect only
 *    once a **different** Legal holder co-signs it (R-108-24), and lapses at
 *    a new grain.
 */
export const VERIFICATION_POLICY_MODES = ['threshold', 'always_verify', 'auto_approve'];
export const VerificationPolicyModeSchema = z.enum(VERIFICATION_POLICY_MODES);
/**
 * `clm.field_verification.policy_disposition` (R-108-4) — why the pipeline
 * queued a new AI-derived field row, or did not. The decision's own order (spec
 * §3.3) is: high stakes, a value contained over its bound, a pin, no co-signed
 * settings, too few decisions, a stale basis, below the threshold, the sample,
 * and only then `auto_approved`.
 *
 * `value_exceeded_bound` is the spec's eight plus one, a build decision of
 * 1 Oct 2026: an admin field whose over-bound value the provider contained as
 * `null` (CLM-109 R-109-12) always reaches a person, because settling that
 * `null` by policy would record that the document is silent when it is not. It
 * describes the extraction, not the field, so it is not a page state
 * ({@link VERIFICATION_POLICY_STATES}).
 *
 * Set once at insert and immutable; `null` on rows enqueued before CLM-108 and
 * on manual-origin rows. A reading copy, not the evidence — the NFR-16
 * evidence is the audit log.
 */
export const POLICY_DISPOSITIONS = [
    'auto_approved',
    'sampled',
    'below_threshold',
    'too_few_decisions',
    'stale',
    'high_stakes_exempt',
    'value_exceeded_bound',
    'admin_pinned',
    'not_configured',
];
export const PolicyDispositionSchema = z.enum(POLICY_DISPOSITIONS);
/**
 * A field's **effective state** on the Auto-approval page: the decision with
 * the sample step dropped (`auto` stands for "sampled or auto-approved, by the
 * bucket"), evaluated at the grain of the field's latest pipeline extraction —
 * the six queue reasons, and `not_extracted` for a field with no extraction
 * (never extracted, or retired).
 */
export const VERIFICATION_POLICY_STATES = [
    'auto',
    'high_stakes_exempt',
    'admin_pinned',
    'not_configured',
    'too_few_decisions',
    'stale',
    'below_threshold',
    'not_extracted',
];
export const VerificationPolicyStateSchema = z.enum(VERIFICATION_POLICY_STATES);
/**
 * A `numeric(4,3)` value in `[0, 1]` as the database renders it. The threshold,
 * the sample rate and a basis rate travel as decimal strings, never as numbers
 * (R-108-5): `Number("0.935") * 1000` need not be an integer, and a float must
 * never touch the threshold. Exactly three decimals, so `0.9` and `0.9000` are
 * refused — the string is the value, not one rendering of it.
 */
export const ThresholdSchema = z.string().regex(/^(0\.\d{3}|1\.000)$/, 'expected a three-decimal rate from 0.000 to 1.000');
/* -------------------------------------------------------------------------- */
/* Notification — §3                                                           */
/* -------------------------------------------------------------------------- */
/**
 * What a notification rule watches. Ratified — `architecture.md` §3.1 and the
 * `notification_rule.watch_type` CHECK constraint in §3.4.
 */
export const NOTIFICATION_WATCH_TYPES = [
    'RenewalNotice',
    'Expiry',
    'TerminationNotice',
    'ObligationDue',
    'UnverifiedField',
];
export const NotificationWatchTypeSchema = z.enum(NOTIFICATION_WATCH_TYPES);
/**
 * Escalation tier. The ladder is ratified (`architecture.md` §3.2): 90 / 60 / 30
 * / 7 days ahead, then daily once past due.
 *
 * TODO(CLM-14): the *encoding* is a judgement call, not a ratified fact.
 * §3.4 stores `tier int NOT NULL -- 1..5`, so the database calls the tiers
 * 1–5 while every human-facing document calls them "90 / 60 / 30 / 7 /
 * past-due". A bare integer on the wire would be unreadable in a URL, an audit
 * row or a bug report, so this contract uses the named form and exports the
 * ordinal mapping below. If PR-05 numbers the tiers differently, change the map,
 * not the wire format.
 */
export const NOTIFICATION_TIERS = ['notice_90', 'notice_60', 'notice_30', 'notice_7', 'past_due'];
export const NotificationTierSchema = z.enum(NOTIFICATION_TIERS);
/** `notification_sent.tier` (§3.4), 1..5. */
export const NOTIFICATION_TIER_ORDINAL = {
    notice_90: 1,
    notice_60: 2,
    notice_30: 3,
    notice_7: 4,
    past_due: 5,
};
/**
 * Days ahead of the occurrence date at which a tier fires. `null` for
 * `past_due`, which re-fires daily until acknowledged rather than at a fixed
 * offset.
 */
export const NOTIFICATION_TIER_DAYS_AHEAD = {
    notice_90: 90,
    notice_60: 60,
    notice_30: 30,
    notice_7: 7,
    past_due: null,
};
/**
 * Who each tier adds. §3.2 — the ladder is cumulative: tier 2 goes to the Client
 * Success owner *and* the Practice Director.
 *
 * These are recipient *rules*, not roles a caller may assert. Actual mailboxes
 * are resolved server-side from Entra scope groups (§3.5).
 */
export const NOTIFICATION_RECIPIENT_RULES = ['owner', 'practice_director', 'pmo', 'elt'];
export const NotificationRecipientRuleSchema = z.enum(NOTIFICATION_RECIPIENT_RULES);
/* -------------------------------------------------------------------------- */
/* Conversion and AI provenance — §4A, §4B, §4C                                */
/* -------------------------------------------------------------------------- */
/**
 * How the document text was produced. §4B.2/§4B.5.
 *
 * `pdf_native_fallback` is modelled separately in `provenance.ts` because it is
 * a **compliance** state, not a technical one: with no extracted text there is
 * nothing to string-replace, so FR-29 pseudonymisation cannot run. It requires
 * logged approval and is never a silent automatic fallback (CLAUDE.md rule 3).
 */
export const CONVERSION_METHODS = ['text_layer', 'ocr', 'pdf_native_fallback'];
export const ConversionMethodSchema = z.enum(CONVERSION_METHODS);
/**
 * Which extraction provider produced a fact. `wp-03` §2.
 *
 * This is a governed vocabulary, not an SDK type — CR-01 requires the evidence
 * chain to show *which model produced which field*, so it has to cross the API
 * boundary. The provider is a config value precisely so the compliance decision
 * stays reversible (§4A.10); the gateway still never calls Claude directly.
 */
export const PROVIDER_IDS = ['anthropic-direct', 'foundry', 'azure-openai'];
export const ProviderIdSchema = z.enum(PROVIDER_IDS);
/**
 * How a contract entered the system. `wp-01` §3.3 `IntakePath`.
 *
 * `Connect` — a DocuSign Connect event. `Manual` — a person at the FR-13 upload
 * form. `Backfill` — discovered by the CLM-43 bulk crawl of already-executed
 * documents, where no envelope arrived and no person chose the individual
 * document.
 *
 * ⚑ **`Backfill` is not ratified.** Migration `0060_contract_intake_path_backfill.sql`
 * widened `ck_contract_intake_path` to accept it and calls the name *"this
 * migration's proposal"*; this export is what makes a backfilled contract
 * serialisable at all, because `ContractDetailSchema.intake_path` is
 * `IntakePathSchema.nullable()` and an unlisted value fails validation on the way
 * out. It is added here in the same change as the writer that creates such rows
 * (CLM-50) rather than ahead of one, so the vocabulary and the only producer of
 * the value land together — but the *name* still wants a yes/no from the
 * developer, with `SharePointBackfill` the alternative 0060 records.
 */
export const INTAKE_PATHS = ['Connect', 'Manual', 'Backfill'];
export const IntakePathSchema = z.enum(INTAKE_PATHS);
/**
 * TODO(CLM-14): vocabulary pending ratification. `security_classification` is
 * required by `architecture.md` §7.1 (and inherited by amendments under §8.1
 * Rule 4) but no document enumerates its values. `wp-01` §3.3 declares it as a
 * free `VARCHAR(20)`.
 *
 * Modelled as a bounded free string rather than an enum so this package does not
 * invent a classification scheme — that is a decision for CEQ-Legal, and it
 * probably has to align with the existing Purview sensitivity labels rather than
 * be minted here.
 */
export const SecurityClassificationSchema = z.string().min(1).max(20);
//# sourceMappingURL=vocabulary.js.map